import { RefObject, useEffect, useState } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { useScrollOver } from '@gluedigital/scrollastic'

import './Pricing.css'

type PlanId = 'preview' | 'explorer' | 'teams' | 'enterprise'

interface Plan {
  id: PlanId
  price: number | null
  period?: 'free' | 'month'
  featured?: boolean
  featureCount: number
  cta: {
    labelId: string
    href: string
  }
}

const REVEAL_POINT = 0.3

const APP_URL = 'https://app.datalab.adigital.org'

const PLANS: Plan[] = [
  {
    id: 'preview',
    price: 0,
    period: 'free',
    featureCount: 5,
    cta: {
      labelId: 'home.pricing.cta.signup',
      href: `${APP_URL}/signup`
    }
  },
  {
    id: 'explorer',
    price: 59,
    period: 'month',
    featureCount: 6,
    cta: {
      labelId: 'home.pricing.cta.info',
      href: `${APP_URL}/signup?plan=explorer`
    }
  },
  {
    id: 'teams',
    price: 299,
    period: 'month',
    featured: true,
    featureCount: 6,
    cta: {
      labelId: 'home.pricing.cta.info',
      href: `${APP_URL}/signup?plan=teams`
    }
  },
  {
    id: 'enterprise',
    price: null,
    featureCount: 5,
    cta: {
      labelId: 'home.pricing.cta.info',
      href: `${APP_URL}/signup?plan=enterprise`
    }
  }
]

function PlanPrice({ price, period }: Pick<Plan, 'price' | 'period'>) {
  if (price === null) {
    return (
      <p className="pricing-card__price pricing-card__price--custom">
        <FormattedMessage id="home.pricing.price.custom" />
      </p>
    )
  }

  return (
    <p className="pricing-card__price">
      <span className="pricing-card__amount">
        <FormattedNumber
          value={price}
          style="currency"
          currency="EUR"
          maximumFractionDigits={0}
        />
      </span>
      {period && (
        <span className="pricing-card__period">
          <FormattedMessage id={`home.pricing.period.${period}`} />
        </span>
      )}
    </p>
  )
}

function PlanCard({ plan }: { plan: Plan }) {
  const base = `home.pricing.plans.${plan.id}`

  return (
    <article
      className={`pricing-card${plan.featured ? ' pricing-card--featured' : ''}`}
    >
      {plan.featured && (
        <span className="pricing-card__badge">
          <FormattedMessage id="home.pricing.badge.popular" />
        </span>
      )}

      <header className="pricing-card__header">
        <span className="pricing-card__icon" aria-hidden="true">
          <img src={`vectors/pricing/${plan.id + '.svg'}`} alt="" />
        </span>
        <div>
          <h3 className="pricing-card__name">
            <FormattedMessage id={`${base}.name`} />
          </h3>
          <p className="pricing-card__description">
            <FormattedMessage id={`${base}.description`} />
          </p>
        </div>
      </header>

      <PlanPrice price={plan.price} period={plan.period} />

      <ul className="pricing-card__features">
        {Array.from({ length: plan.featureCount }, (_, i) => (
          <li key={i} className="pricing-card__feature">
            <FormattedMessage id={`${base}.features.${i}`} />
          </li>
        ))}
      </ul>

      <a
        className="pricing-card__cta"
        href={plan.cta.href}
        target="_blank"
        rel="noreferrer noopener"
      >
        <FormattedMessage id={plan.cta.labelId} />
      </a>
    </article>
  )
}

interface PricingProps {
  scrollable?: RefObject<HTMLDivElement | null>
}

export default function Pricing({ scrollable }: PricingProps) {
  const scrollOver = useScrollOver(
    '#pricing',
    scrollable as RefObject<HTMLElement>
  )
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    if (scrollOver >= REVEAL_POINT) setRevealed(true)
  }, [scrollOver])

  return (
    <section id="pricing" className="landing-section">
      <div className="element element-circle" />
      <div className="container">
        <h2>
          <FormattedMessage id="home.pricing.subtitle" />
        </h2>
        <h1>
          <FormattedMessage id="home.pricing.title" />
        </h1>

        <div className={`pricing-grid${revealed ? ' is-visible' : ''}`}>
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      </div>
    </section>
  )
}
